import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { createRegisterSchema } from "@/schemas/registerSchema";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import authService from "@/services/authService";
import legalContentService, {
  type LegalContent,
} from "@/services/legalContentService";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { HelpCircle, Mail, User, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "@/hooks/useTheme";
import { useAppDispatch } from "@/hooks/redux";
import { checkAuthStatus } from "@/store/slices/authSlice";
// Step type for the registration + OTP flow
type RegistrationStep = "personalInfo" | "accountCredentials" | "otp";

export function RegisterForm() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [registrationStep, setRegistrationStep] =
    useState<RegistrationStep>("personalInfo");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [isContactSupportOpen, setIsContactSupportOpen] = useState(false);
  const { isDark } = useTheme();
  // Legal content state
  const [termsContent, setTermsContent] = useState<LegalContent | null>(null);
  const [loadingTerms, setLoadingTerms] = useState(true);

  // Fetch terms and conditions from database
  React.useEffect(() => {
    const fetchTermsContent = async () => {
      try {
        const contents = await legalContentService.getAllLegalContents();
        const terms = contents.find((content) => content.type === "terms");
        if (terms) {
          setTermsContent(terms);
        }
      } catch (error) {
        console.error("Error fetching terms content:", error);
        // Keep termsContent as null to fallback to static content
      } finally {
        setLoadingTerms(false);
      }
    };

    fetchTermsContent();
  }, []);

  // Helper function to get localized content
  const getLocalizedTermsContent = () => {
    if (!termsContent) {
      return null; // Will fallback to static content
    }
    return i18n.language === "fr"
      ? termsContent.frContent
      : termsContent.enContent;
  };

  // Helper function to open email client
  const handleContactSupport = () => {
    const email = import.meta.env.VITE_SUPPORT_EMAIL;
    const subject = encodeURIComponent(
      `Demande de Support - ${import.meta.env.VITE_APP_NAME}`,
    );
    const currentDate = new Date().toLocaleDateString("fr-FR");
    const currentTime = new Date().toLocaleTimeString("fr-FR");

    const body = encodeURIComponent(
      `Bonjour l'équipe Support,

J'ai besoin d'assistance concernant :
[Veuillez décrire votre problème ou question ici]


TYPE DE PROBLÈME :
□ Problème de connexion / authentification
                    □ Problème technique
□ Problème de facturation / abonnement
□ Problème technique (bugs, erreurs)
□ Question sur l'utilisation
□ Demande de fonctionnalité
□ Autre : _______________


DÉTAILS TECHNIQUES :
- Navigateur utilisé : ${
        navigator.userAgent.includes("Chrome")
          ? "Chrome"
          : navigator.userAgent.includes("Firefox")
            ? "Firefox"
            : navigator.userAgent.includes("Safari")
              ? "Safari"
              : navigator.userAgent.includes("Edge")
                ? "Edge"
                : "Autre"
      }
- Système d'exploitation : ${navigator.platform || "Non déterminé"}
- Date et heure : ${currentDate} à ${currentTime}
- URL de la page concernée : ${window.location.href}


INFORMATIONS UTILISATEUR :
- Email : Non renseigné (page d'inscription)
- Nom complet : Non renseigné (page d'inscription)
- Statut : Tentative d'inscription


ÉTAPES POUR REPRODUIRE LE PROBLÈME :
1. 
2. 
3. 

RÉSULTAT ATTENDU :


RÉSULTAT OBTENU :


Merci pour votre aide et votre support.

Cordialement`,
    );

    // Create mailto link
    const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;

    // Open email client
    window.location.href = mailtoLink;
  };

  // User info form validation
  const userForm = useFormik({
    initialValues: {
      userType: "individual" as "individual" | "company",
      firstName: "",
      lastName: "",
      phone: "",
      address: "",
      city: "",
      postalCode: "",
      companyName: "",
      siret: "",
      email: "",
      confirmEmail: "",
      password: "",
      confirmPassword: "",
      termsAccepted: false,
    },
    validationSchema: createRegisterSchema(t),
    onSubmit: async (values) => {
      // This handler is only for the credentials step and OTP request
      if (registrationStep === "accountCredentials") {
        setIsLoading(true);
        setError(null);
        try {
          // Request OTP only after both steps are completed
          const response = await authService.requestRegistrationOTP({
            userType: values.userType,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            password: values.password,
            phone: values.phone,
            address: values.address,
            city: values.city,
            postalCode: values.postalCode,
            companyName: values.companyName,
            siret: values.siret,
          });

          // Store the userId for OTP verification
          setUserId(response.userId);
          setRegistrationStep("otp");

          // DEV ONLY: Show OTP in toast for debugging. Remove in production.
          if (response.otp && process.env.NODE_ENV === "development") {
            toast.info(`Code OTP (dev): ${response.otp}`);
          }
        } catch (error) {
          if (error instanceof Error) {
            setError(error.message);
          } else {
            setError(t("auth.registrationError"));
          }
        } finally {
          setIsLoading(false);
        }
      }
    },
  });

  // Handler for the first step "continue" button
  const handleContinueToCredentials = () => {
    // Validate first step fields based on user type
    if (userForm.values.userType === "individual") {
      if (!userForm.values.firstName || !userForm.values.lastName) {
        // Touch the fields to trigger validation errors
        userForm.setFieldTouched("firstName", true);
        userForm.setFieldTouched("lastName", true);
        return;
      }
    } else if (userForm.values.userType === "company") {
      if (!userForm.values.companyName || !userForm.values.siret) {
        // Touch the fields to trigger validation errors
        userForm.setFieldTouched("companyName", true);
        userForm.setFieldTouched("siret", true);
        return;
      }
    }

    // Validate address fields (required for all users)
    if (
      !userForm.values.address ||
      !userForm.values.city ||
      !userForm.values.postalCode
    ) {
      userForm.setFieldTouched("address", true);
      userForm.setFieldTouched("city", true);
      userForm.setFieldTouched("postalCode", true);
      return;
    }

    // Clear any previous errors
    setError(null);

    // Proceed to next step
    setRegistrationStep("accountCredentials");
  };

  // OTP form validation
  const otpForm = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: Yup.object({
      otp: Yup.string()
        .matches(/^\d{6}$/, t("validation.otpFormat"))
        .required(t("validation.otpRequired")),
    }),
    onSubmit: async (values) => {
      if (!userId) {
        setError(t("auth.sessionExpired"));
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        // Verify OTP to complete registration
        await authService.verifyRegistrationOTP({
          userId: userId,
          otpCode: values.otp,
        });

        // ✅ Récupérer les données utilisateur complètes
        await dispatch(checkAuthStatus());

        // Registration successful, redirect to home
        navigate("/plans");
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(t("auth.registrationVerificationError"));
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Reset to previous step
  const handlePreviousStep = () => {
    if (registrationStep === "accountCredentials") {
      setRegistrationStep("personalInfo");
    } else if (registrationStep === "otp") {
      setRegistrationStep("accountCredentials");
    }
    setError(null);
  };

  // Get step title
  const getStepTitle = () => {
    switch (registrationStep) {
      case "personalInfo":
        return t("auth.personalInformation");
      case "accountCredentials":
        return t("auth.accountCredentials");
      case "otp":
        return t("auth.verifyEmail");
      default:
        return t("auth.createAccount");
    }
  };

  // Get step description
  const getStepDescription = () => {
    switch (registrationStep) {
      case "personalInfo":
        return t("auth.tellUsAboutYourself");
      case "accountCredentials":
        return t("auth.setupLoginDetails");
      case "otp":
        return t("auth.otpSentTo", { email: userForm.values.email });
      default:
        return t("auth.enterInfoToCreateAccount");
    }
  };

  // Render progress indicators
  const renderProgressIndicator = () => {
    const steps = ["personalInfo", "accountCredentials", "otp"];
    const currentIndex = steps.indexOf(registrationStep);

    return (
      <div className="flex w-full justify-center gap-2 mb-4">
        {steps.map((step, index) => (
          <div key={step} className="flex flex-1 flex-col items-center">
            <div
              className={`w-full h-2 rounded-full flex items-center justify-center text-xs font-medium
                ${
                  index <= currentIndex
                    ? "bg-amber-500 text-primary-foreground"
                    : "bg-slate-200 dark:bg-slate-800 text-muted-foreground"
                }`}
            />
          </div>
        ))}
      </div>
    );
  };

  // Render form based on current step
  const renderStepForm = () => {
    switch (registrationStep) {
      case "personalInfo":
        return (
          <div className="grid gap-6">
            <div className="grid gap-4">
              {/* Sélection du type d'utilisateur */}
              <div className="grid gap-3">
                <Label>{t("auth.userType")} *</Label>
                <Select
                  value={userForm.values.userType}
                  onValueChange={(value: "individual" | "company") =>
                    userForm.setFieldValue("userType", value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t("auth.selectUserType")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">
                      <div className="flex items-center gap-3">
                        <User className="h-4 w-4" />
                        <div className="flex items-center gap-1">
                          <div className="font-medium">
                            {t("auth.individual")}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {`(${t("auth.individualDescription")})`}
                          </div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="company">
                      <div className="flex items-center gap-3">
                        <Building2 className="h-4 w-4" />
                        <div className="flex items-center gap-1">
                          <div className="font-medium">{t("auth.company")}</div>
                          <div className="text-xs text-muted-foreground">
                            {`(${t("auth.companyDescription")})`}
                          </div>
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {userForm.touched.userType && userForm.errors.userType && (
                  <p className="text-sm text-destructive">
                    {userForm.errors.userType}
                  </p>
                )}
              </div>

              <Separator className="my-2" />

              {/* Champs spécifiques selon le type d'utilisateur */}
              {userForm.values.userType === "individual" ? (
                /* Champs pour particulier */
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="firstName">{t("auth.firstName")} *</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={userForm.values.firstName}
                      onChange={userForm.handleChange}
                      onBlur={userForm.handleBlur}
                      disabled={isLoading}
                      placeholder={t("auth.firstNamePlaceholder")}
                      maxLength={50}
                    />
                    {userForm.touched.firstName &&
                      userForm.errors.firstName && (
                        <p className="text-sm text-destructive">
                          {userForm.errors.firstName}
                        </p>
                      )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="lastName">{t("auth.lastName")} *</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={userForm.values.lastName}
                      onChange={userForm.handleChange}
                      onBlur={userForm.handleBlur}
                      disabled={isLoading}
                      placeholder={t("auth.lastNamePlaceholder")}
                      maxLength={50}
                    />
                    {userForm.touched.lastName && userForm.errors.lastName && (
                      <p className="text-sm text-destructive">
                        {userForm.errors.lastName}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                /* Champs pour entreprise */
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="companyName">
                      {t("auth.companyName")} *
                    </Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      value={userForm.values.companyName}
                      onChange={userForm.handleChange}
                      onBlur={userForm.handleBlur}
                      disabled={isLoading}
                      placeholder="ex: ACME Corporation"
                      maxLength={100}
                    />
                    {userForm.touched.companyName &&
                      userForm.errors.companyName && (
                        <p className="text-sm text-destructive">
                          {userForm.errors.companyName}
                        </p>
                      )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="siret">{t("auth.siret")} *</Label>
                    <Input
                      id="siret"
                      name="siret"
                      value={userForm.values.siret}
                      onChange={userForm.handleChange}
                      onBlur={userForm.handleBlur}
                      disabled={isLoading}
                      placeholder="ex: 12345678901234"
                      maxLength={14}
                    />
                    {userForm.touched.siret && userForm.errors.siret && (
                      <p className="text-sm text-destructive">
                        {userForm.errors.siret}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {t("auth.siretFormat")}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid gap-2">
                <Label htmlFor="phone">{t("auth.phone")} *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={userForm.values.phone}
                  onChange={userForm.handleChange}
                  onBlur={userForm.handleBlur}
                  disabled={isLoading}
                  placeholder="ex: +33123456789"
                  maxLength={15}
                />
                {userForm.touched.phone && userForm.errors.phone && (
                  <p className="text-sm text-destructive">
                    {userForm.errors.phone}
                  </p>
                )}
              </div>

              <Separator className="my-2" />
              <h3 className="text-sm font-medium mb-2">
                {t("auth.billingAddress")} *
              </h3>

              <div className="grid gap-2">
                <Label htmlFor="address">{t("auth.streetAddress")} *</Label>
                <Input
                  id="address"
                  name="address"
                  value={userForm.values.address}
                  onChange={userForm.handleChange}
                  onBlur={userForm.handleBlur}
                  disabled={isLoading}
                  placeholder="ex: 123 rue de la Paix"
                  maxLength={100}
                />
                {userForm.touched.address && userForm.errors.address && (
                  <p className="text-sm text-destructive">
                    {userForm.errors.address}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="city">{t("auth.city")} *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={userForm.values.city}
                    onChange={userForm.handleChange}
                    onBlur={userForm.handleBlur}
                    disabled={isLoading}
                    placeholder="ex: Paris"
                    maxLength={30}
                  />
                  {userForm.touched.city && userForm.errors.city && (
                    <p className="text-sm text-destructive">
                      {userForm.errors.city}
                    </p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="postalCode">{t("auth.postalCode")} *</Label>
                  <Input
                    id="postalCode"
                    name="postalCode"
                    value={userForm.values.postalCode}
                    onChange={userForm.handleChange}
                    onBlur={userForm.handleBlur}
                    disabled={isLoading}
                    placeholder="ex: 75001"
                    maxLength={5}
                  />
                  {userForm.touched.postalCode &&
                    userForm.errors.postalCode && (
                      <p className="text-sm text-destructive">
                        {userForm.errors.postalCode}
                      </p>
                    )}
                </div>
              </div>

              <p className="text-sm text-muted-foreground mt-2">
                {t("auth.requiredFieldsNote")}
              </p>
            </div>

            <Button
              className="w-full"
              type="button"
              onClick={handleContinueToCredentials}
              disabled={
                isLoading ||
                !userForm.values.phone ||
                !userForm.values.address ||
                !userForm.values.city ||
                !userForm.values.postalCode ||
                (userForm.values.userType === "individual" &&
                  (!userForm.values.firstName || !userForm.values.lastName)) ||
                (userForm.values.userType === "company" &&
                  (!userForm.values.companyName || !userForm.values.siret))
              }
            >
              {t("auth.continueToAccountSetup")}
            </Button>
          </div>
        );

      case "accountCredentials":
        return (
          <div className="grid gap-6">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">{t("auth.email")}*</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={userForm.values.email}
                  onChange={userForm.handleChange}
                  onBlur={userForm.handleBlur}
                  disabled={isLoading}
                  placeholder="ex: exemple@email.com"
                  maxLength={100}
                />
                {userForm.touched.email && userForm.errors.email && (
                  <p className="text-sm text-destructive">
                    {userForm.errors.email}
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="confirmEmail">{t("auth.confirmEmail")}*</Label>
                <Input
                  id="confirmEmail"
                  name="confirmEmail"
                  type="email"
                  value={userForm.values.confirmEmail}
                  onChange={userForm.handleChange}
                  onBlur={userForm.handleBlur}
                  disabled={isLoading}
                  placeholder="ex: exemple@email.com"
                  maxLength={100}
                />
                {userForm.touched.confirmEmail &&
                  userForm.errors.confirmEmail && (
                    <p className="text-sm text-destructive">
                      {userForm.errors.confirmEmail}
                    </p>
                  )}
              </div>

              <Separator className="my-2" />

              <div className="grid gap-2">
                <Label htmlFor="password">{t("auth.password")}*</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={userForm.values.password}
                  onChange={userForm.handleChange}
                  onBlur={userForm.handleBlur}
                  disabled={isLoading}
                  maxLength={128}
                />
                {userForm.touched.password && userForm.errors.password && (
                  <p className="text-sm text-destructive">
                    {userForm.errors.password}
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="confirmPassword">
                  {t("auth.confirmPassword")}*
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={userForm.values.confirmPassword}
                  onChange={userForm.handleChange}
                  onBlur={userForm.handleBlur}
                  disabled={isLoading}
                  maxLength={128}
                />
                {userForm.touched.confirmPassword &&
                  userForm.errors.confirmPassword && (
                    <p className="text-sm text-destructive">
                      {userForm.errors.confirmPassword}
                    </p>
                  )}
              </div>

              <div className="flex items-center space-x-2 mt-2">
                <Checkbox
                  id="termsAccepted"
                  name="termsAccepted"
                  checked={userForm.values.termsAccepted}
                  onCheckedChange={(checked: boolean) => {
                    userForm.setFieldValue("termsAccepted", checked);
                  }}
                  disabled={isLoading}
                />
                <label
                  htmlFor="termsAccepted"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t("auth.acceptTerms")}{" "}
                  <button
                    type="button"
                    onClick={() => setShowTermsDialog(true)}
                    className="text-primary underline hover:text-primary/80 transition-colors"
                  >
                    {t("auth.termsAndConditions")}
                  </button>
                </label>
              </div>
              {userForm.touched.termsAccepted &&
                userForm.errors.termsAccepted && (
                  <p className="text-sm text-destructive">
                    {userForm.errors.termsAccepted}
                  </p>
                )}

              <p className="text-sm text-muted-foreground mt-2">
                {t("auth.requiredFieldsNote")}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                type="button"
                onClick={handlePreviousStep}
                disabled={isLoading}
              >
                {t("common.back")}
              </Button>
              <Button
                className="flex-1"
                type="submit"
                disabled={
                  isLoading ||
                  !userForm.values.email ||
                  !userForm.values.phone ||
                  !userForm.values.confirmEmail ||
                  userForm.values.email !== userForm.values.confirmEmail ||
                  !userForm.values.password ||
                  !userForm.values.confirmPassword ||
                  userForm.values.password !==
                    userForm.values.confirmPassword ||
                  !userForm.values.termsAccepted
                }
              >
                {isLoading ? t("common.processing") : t("auth.createAccount")}
              </Button>
            </div>
          </div>
        );

      case "otp":
        return (
          <form onSubmit={otpForm.handleSubmit}>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="otp">{t("auth.verificationCode")}</Label>
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otpForm.values.otp}
                    onChange={(value: string) =>
                      otpForm.setFieldValue("otp", value)
                    }
                    disabled={isLoading}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {otpForm.touched.otp && otpForm.errors.otp && (
                  <p className="text-sm text-destructive">
                    {otpForm.errors.otp}
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  type="button"
                  onClick={handlePreviousStep}
                  disabled={isLoading}
                >
                  {t("common.back")}
                </Button>
                <Button
                  className="flex-1"
                  type="submit"
                  disabled={
                    isLoading ||
                    !otpForm.values.otp ||
                    otpForm.values.otp.length < 6
                  }
                >
                  {isLoading
                    ? t("common.verifying")
                    : t("auth.completeRegistration")}
                </Button>
              </div>
            </div>
          </form>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Card className="w-full">
        <CardHeader className="space-y-1">
          <div className="flex justify-center gap-2 mb-4">
            <Link to="/" className="flex justify-center gap-2 mb-4">
              <img
                src={
                  isDark
                    ? "/assets/logo_full_dark.webp"
                    : "/assets/logo_full_light.webp"
                }
                alt="logo"
                className="h-14 transition-all duration-300 "
              />
            </Link>
          </div>

          {renderProgressIndicator()}

          <CardTitle className="text-2xl text-center">
            {getStepTitle()}
          </CardTitle>
          <CardDescription className="text-center">
            {getStepDescription()}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {error && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
              {error}
            </div>
          )}

          {registrationStep === "accountCredentials" ? (
            <form onSubmit={userForm.handleSubmit}>{renderStepForm()}</form>
          ) : (
            renderStepForm()
          )}
        </CardContent>
        <CardFooter className="flex flex-col">
          {/* Contact Support Dialog */}
          <Dialog
            open={isContactSupportOpen}
            onOpenChange={setIsContactSupportOpen}
          >
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="mb-3 gap-2">
                <HelpCircle className="h-4 w-4" />
                {t("dashboard.contactSupport")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  {t("dashboard.contactSupport")}
                </DialogTitle>
                <DialogDescription>
                  {t("dashboard.contactSupportDescription")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex flex-col space-y-3">
                  <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Email Support</p>
                        <p className="text-sm text-muted-foreground">
                          {import.meta.env.VITE_SUPPORT_EMAIL}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button onClick={handleContactSupport} className="w-full">
                    <Mail className="h-4 w-4 mr-2" />
                    {t("dashboard.openEmailApp")}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center">
                    {t("dashboard.emailAppNote")}
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <div className="mt-2 text-center text-sm">
            {t("auth.alreadyHaveAccount")}{" "}
            <a href="/login" className="underline text-primary">
              {t("auth.signIn")}
            </a>
          </div>
        </CardFooter>
      </Card>

      {/* Terms and Conditions Dialog */}
      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("auth.termsAndConditions")}</DialogTitle>
            <DialogDescription>{t("auth.pleaseReadTerms")}</DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-6 text-sm">
            {!loadingTerms && termsContent ? (
              // Display content from database
              <div className="prose dark:prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {getLocalizedTermsContent()}
                </div>
              </div>
            ) : (
              // Fallback to static content if database content is not available
              <>
                <section>
                  <h3 className="font-semibold text-base mb-2">
                    1. {t("terms.acceptance")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("terms.acceptanceText")}
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">
                    2. {t("terms.description")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("terms.descriptionText")}
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">
                    3. {t("terms.userAccounts")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("terms.userAccountsText")}
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">
                    4. {t("terms.paymentTerms")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("terms.paymentTermsText")}
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">
                    5. {t("terms.privacy")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("terms.privacyText")}
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">
                    6. {t("terms.limitations")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("terms.limitationsText")}
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">
                    7. {t("terms.termination")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("terms.terminationText")}
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">
                    8. {t("terms.modifications")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("terms.modificationsText")}
                  </p>
                </section>

                <section>
                  <h3 className="font-semibold text-base mb-2">
                    9. {t("terms.contact")}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {t("terms.contactText", {
                      supportEmail: import.meta.env.VITE_SUPPORT_EMAIL,
                    })}
                  </p>
                </section>
              </>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowTermsDialog(false)}>
              {t("common.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
